import { CardDef, CardInstance, PlayerState, BotDifficulty, Venue, CardTier } from '../types';
import { getCardById } from '../cards';
import { DeckPhase } from './DeckPhase';

type BotStrategy = 'balanced' | 'aggressive' | 'synergy' | 'random';

export class BotAI {
  private difficulty: BotDifficulty;
  private strategy: BotStrategy;
  private preferredCategory: string | null = null;

  constructor(difficulty: BotDifficulty = 'normal') {
    this.difficulty = difficulty;
    this.strategy = this.pickStrategy();
  }

  private pickStrategy(): BotStrategy {
    switch (this.difficulty) {
      case 'easy':
        return 'random';
      case 'normal':
        return Math.random() < 0.5 ? 'balanced' : 'synergy';
      case 'hard':
        return Math.random() < 0.7 ? 'synergy' : 'aggressive';
      default:
        return 'balanced';
    }
  }

  chooseVenue(venues: Venue[], player: PlayerState): Venue {
    if (this.difficulty === 'easy') {
      return venues[Math.floor(Math.random() * venues.length)];
    }

    const categoryCount = this.countCategories(player.deck);
    
    let preferred = this.preferredCategory;
    if (!preferred) {
      const sorted = Object.entries(categoryCount).sort((a, b) => b[1] - a[1]);
      preferred = sorted[0]?.[0] ?? 'warrior';
      this.preferredCategory = preferred;
    }

    const venueMap: Record<string, string> = {
      warrior: 'arena',
      thief: 'backstreet',
      mage: 'tower',
      machine: 'factory',
      undead: 'graveyard',
    };

    const preferredVenueId = venueMap[preferred];
    const preferredVenue = venues.find((v) => v.id === preferredVenueId);

    if (preferredVenue && Math.random() < 0.7) {
      return preferredVenue;
    }

    const market = venues.find((v) => v.id === 'market');
    if (market && Math.random() < 0.3) {
      return market;
    }

    return venues[Math.floor(Math.random() * venues.length)];
  }

  chooseCard(choices: CardDef[], player: PlayerState): CardDef | null {
    if (choices.length === 0) return null;

    if (this.difficulty === 'easy') {
      return choices[Math.floor(Math.random() * choices.length)];
    }

    switch (this.strategy) {
      case 'aggressive':
        return choices.reduce((best, c) => 
          c.basePower > best.basePower ? c : best
        );

      case 'synergy':
        const preferred = this.preferredCategory;
        const synergistic = choices.filter((c) => c.category === preferred);
        if (synergistic.length > 0) {
          return synergistic[Math.floor(Math.random() * synergistic.length)];
        }
        return choices[Math.floor(Math.random() * choices.length)];

      case 'balanced':
        const withAbilities = choices.filter((c) => c.abilities.length > 0);
        if (withAbilities.length > 0 && Math.random() < 0.6) {
          return withAbilities[Math.floor(Math.random() * withAbilities.length)];
        }
        return choices[Math.floor(Math.random() * choices.length)];

      default:
        return choices[Math.floor(Math.random() * choices.length)];
    }
  }

  shouldRemoveCard(player: PlayerState, deckPhase: DeckPhase): CardInstance | null {
    // デッキが12枚を超えたら除外を検討
    if (player.deck.length > 12) {
      return this.chooseCardToRemove(player);
    }

    if (this.difficulty !== 'hard') {
      return null;
    }

    const deckInfo = deckPhase.getDeckInfo(player);
    if (deckInfo.cards.length > 8) {
      return this.chooseCardToRemove(player);
    }

    return null;
  }

  private chooseCardToRemove(player: PlayerState): CardInstance | null {
    const categoryCount = this.countCategories(player.deck);
    
    let weakest: CardInstance | null = null;
    let weakestScore = Infinity;

    for (const card of player.deck) {
      const def = getCardById(card.defId);
      if (!def) continue;

      const catCount = categoryCount[def.category] ?? 0;
      const score = card.currentPower + catCount * 2;

      if (score < weakestScore) {
        weakestScore = score;
        weakest = card;
      }
    }

    return weakest;
  }

  private countCategories(deck: CardInstance[]): Record<string, number> {
    const count: Record<string, number> = {};
    for (const card of deck) {
      const def = getCardById(card.defId);
      if (def) {
        count[def.category] = (count[def.category] ?? 0) + 1;
      }
    }
    return count;
  }

  processDeckPhase(player: PlayerState, deckPhase: DeckPhase): void {
    const categoryCount = this.countCategories(player.deck);
    const availableTiers = deckPhase.getAvailableTiers();
    
    // 最も有利なTierを選択
    let bestTier = availableTiers[0];
    let bestScore = -1;
    
    for (const tierOpt of availableTiers) {
      // Tierの価値を評価 (高Tierほど良い、枚数が多いほど良い)
      const tierValue = tierOpt.tier === 'C' ? 3 : tierOpt.tier === 'B' ? 2 : 1;
      const score = tierValue * 10 + tierOpt.selectCount * 5;
      if (score > bestScore) {
        bestScore = score;
        bestTier = tierOpt;
      }
    }
    
    // Tierを選択してカードを引く
    const drawnCards = deckPhase.selectTier(player.info.id, bestTier.tier);
    if (!drawnCards) return;
    
    // スコアリング
    const scored = drawnCards.map((card: CardDef) => ({
      card,
      score: this.calculateCardScore(card, categoryCount, player.deck.length),
    })).sort((a: { card: CardDef; score: number }, b: { card: CardDef; score: number }) => b.score - a.score);
    
    // 上位カードを選択
    const selectedIds: string[] = [];
    for (let i = 0; i < Math.min(bestTier.selectCount, scored.length); i++) {
      selectedIds.push(scored[i].card.id);
    }
    
    // 選択を確定
    deckPhase.addSelectedCards(player, selectedIds);

    // デッキ整理 (オプション)
    const toRemove = this.shouldRemoveCard(player, deckPhase);
    if (toRemove) {
      deckPhase.removeCard(player, toRemove.instanceId);
    }
  }

  private calculateCardScore(card: CardDef, categoryCount: Record<string, number>, deckSize: number): number {
    let score = card.basePower;
    
    // シナジーボーナス
    if ((categoryCount[card.category] ?? 0) >= 2) {
      score += 5;
    }
    
    // 能力持ちはボーナス
    if (card.abilities.length > 0) {
      score += 3;
    }
    
    // Tierボーナス
    if (card.tier === 'C') score += 4;
    else if (card.tier === 'B') score += 2;
    
    return score;
  }
}

export function createBotPlayer(
  index: number,
  difficulty: BotDifficulty = 'normal'
): PlayerState {
  const names = [
    'Bot Alpha', 'Bot Beta', 'Bot Gamma', 'Bot Delta',
    'Bot Epsilon', 'Bot Zeta', 'Bot Eta', 'Bot Theta',
  ];

  return {
    info: {
      id: `bot_${index}_${Date.now()}`,
      name: names[index % names.length],
      type: 'bot',
      botDifficulty: difficulty,
    },
    deck: [],
    bench: [],
    benchLimit: 6,
    fans: 0,
    matchHistory: [],
    isEliminated: false,
  };
}
